// Página Products - Catálogo de productos
// Muestra todos los productos con filtros, búsqueda y ordenamiento

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useProducts } from '../hooks/useFirestore'
import { useCategories } from '../hooks/useCategories'
import { useCart } from '../hooks/useCart'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { getHelpSettings } from '../services/helpSettings.service'
import { getImageUrl } from '../services/cloudinary.service'
import { buildWhatsAppLinkAR } from '../utils/whatsapp'
import Toast from '../components/Toast'

const buildEmbedUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
  if (raw.includes('facebook.com')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantMedia = (product, transformations) => {
  const media = []
  const seen = new Set()
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
    const variantVideos = Array.isArray(variant?.videos) ? variant.videos : []
    variantVideos.forEach(video => {
      const embedUrl = buildEmbedUrl(video)
      if (!embedUrl) return
      const key = `video:${embedUrl}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'video', src: embedUrl })
      }
    })
  })
  if (media.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
  }
  return media
}

function VariantMediaCarousel({ media, alt, className }) {
  const safeMedia = useMemo(() => (media.length > 0 ? media : [{ type: 'image', src: getImageUrl(null) }]), [media])
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [media])

  useEffect(() => {
    if (safeMedia.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % safeMedia.length)
    }, 3500)
    return () => clearInterval(id)
  }, [safeMedia])

  return (
    <div className={`relative w-full h-full overflow-hidden ${className || ''}`}>
      <div
        className="flex h-full w-full transition-transform duration-500"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {safeMedia.map((item, idx) => (
          <div key={`${item.type}-${item.src}-${idx}`} className="h-full w-full flex-shrink-0">
            {item.type === 'video' ? (
              <iframe
                src={item.src}
                title={`Video ${idx + 1}`}
                className="h-full w-full pointer-events-none"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              <img
                src={item.src}
                alt={alt}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            )}
          </div>
        ))}
      </div>
      {safeMedia.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {safeMedia.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`h-2 w-2 rounded-full border border-white/80 ${idx === index ? 'bg-white' : 'bg-white/50'}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIndex(idx)
              }}
              aria-label={`Imagen ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Página Products
 * Catálogo completo de productos con filtros y búsqueda
 * Diseño basado en mockup Pine & Co
 */
function Products() {
  const navigate = useNavigate()
  // Obtener categorías dinámicas
  const { categories } = useCategories()
  
  // Hook del carrito
  const { addToCart } = useCart()

  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  
  // Estado para notificaciones
  const [notification, setNotification] = useState(null)
  const [helpSettings, setHelpSettings] = useState(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await getHelpSettings()
        if (mounted) setHelpSettings(data)
      } catch {
        if (mounted) setHelpSettings(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  // Estado para los filtros
  const [selectedCategory, setSelectedCategory] = useState('')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('createdAt') // createdAt, price, name
  const [sortOrder, setSortOrder] = useState('desc') // asc, desc
  
  // Estado para UI de filtros (colapsables)
  const [showCategoryFilter, setShowCategoryFilter] = useState(true)
  const [showPriceFilter, setShowPriceFilter] = useState(true)

  // Construir opciones para la consulta de Firestore
  const firestoreOptions = useMemo(() => {
    const options = {
      categoryId: selectedCategory || null,
      minPrice: minPrice ? parseFloat(minPrice) : null,
      maxPrice: maxPrice ? parseFloat(maxPrice) : null,
      orderByField: sortBy,
      orderDirection: sortOrder,
      limitResults: null
    }
    return options
  }, [selectedCategory, minPrice, maxPrice, sortBy, sortOrder])

  // Obtener productos desde Firestore
  const { products, loading } = useProducts(
    firestoreOptions,
    [selectedCategory, minPrice, maxPrice, sortBy, sortOrder]
  )

  // Filtrar productos por búsqueda (cliente)
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      const base = products
      if (outOfStockPolicy.visibility === 'hide') return base.filter(p => (p?.stock || 0) > 0)
      return base
    }

    const query = searchQuery.toLowerCase()
    const base = products.filter(product => {
      const name = (product.name || '').toLowerCase()
      const description = (product.description || '').toLowerCase()
      const category = (product.category || '').toLowerCase()
      
      return name.includes(query) || 
             description.includes(query) || 
             category.includes(query)
    })
    if (outOfStockPolicy.visibility === 'hide') return base.filter(p => (p?.stock || 0) > 0)
    return base
  }, [products, searchQuery, outOfStockPolicy.visibility])

  // Limpiar todos los filtros
  const clearFilters = () => {
    setSelectedCategory('')
    setMinPrice('')
    setMaxPrice('')
    setSearchQuery('')
    setSortBy('createdAt')
    setSortOrder('desc')
  }

  // Manejar agregar al carrito
  const handleAddToCart = (e, product) => {
    e.preventDefault() // Prevenir navegación al detalle
    e.stopPropagation()

    const stock = product?.stock || 0
    if (stock <= 0 && outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
      if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
        const whatsapp = helpSettings?.contact?.socialMedia?.whatsapp
        const url = typeof window !== 'undefined'
          ? `${window.location.origin}/productos/${product.id}`
          : ''
        const text = `Hola! Quiero consultar por el producto: ${product?.name || 'Producto'}. Link: ${url}`
        const href = buildWhatsAppLinkAR(whatsapp, text)
        setNotification({ type: 'error', message: 'Este producto no tiene stock. Disponible solo para consulta.', duration: 5000 })
        if (href) {
          window.open(href, '_blank', 'noreferrer')
        } else {
          navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
        }
        return
      }
      setNotification({ type: 'error', message: 'Este producto no tiene stock disponible.', duration: 5000 })
      return
    }

    addToCart(product, null, 1)

    setNotification({
      type: 'success',
      message: `Agregado al carrito: ${product.name}`,
      duration: 6000,
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') }
    })
  }

  // Manejar cambios en el ordenamiento desde el select combinado
  const handleSortChange = (e) => {
    const value = e.target.value
    switch(value) {
      case 'relevance':
      case 'newest':
        setSortBy('createdAt')
        setSortOrder('desc')
        break
      case 'price_asc':
        setSortBy('price')
        setSortOrder('asc')
        break
      case 'price_desc':
        setSortBy('price')
        setSortOrder('desc')
        break
      case 'name_asc':
        setSortBy('name')
        setSortOrder('asc')
        break
      default:
        setSortBy('createdAt')
        setSortOrder('desc')
    }
  }

  // Obtener nombre de la categoría actual para el título
  const currentCategoryName = useMemo(() => {
    if (!selectedCategory) return 'Todos los Productos'
    const category = categories.find(c => c.id === selectedCategory)
    return category ? category.name : 'Productos'
  }, [selectedCategory, categories])

  return (
    <div className="flex flex-col min-h-screen w-full bg-background-light dark:bg-background-dark text-text-dark dark:text-text-light font-sans">
      {/* Toast de notificación */}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'success'}
        duration={notification?.duration ?? 3000}
        actionLabel={notification?.action?.label}
        onAction={notification?.action?.onClick}
        onClose={() => setNotification(null)}
      />

      <main className="flex-1 w-full mx-auto max-w-[1280px] px-4 md:px-10 pb-20 pt-8">
        <div className="flex flex-col lg:flex-row gap-10">
          
          {/* Sidebar de Filtros */}
          <aside className="w-full lg:w-64 flex-shrink-0 flex flex-col gap-8">
            
            {/* Buscador */}
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium tracking-tight text-gray-900 dark:text-white">Buscar Productos</label>
              <div className="flex w-full items-center rounded-lg bg-accent-light dark:bg-gray-800 border border-transparent focus-within:border-primary/50 transition-colors">
                <div className="text-primary flex items-center justify-center pl-3">
                  <span className="material-symbols-outlined text-[18px]">search</span>
                </div>
                <input 
                  className="w-full bg-transparent border-none text-sm p-2.5 focus:ring-0 placeholder:text-gray-400 font-light focus:outline-none" 
                  placeholder="Ej: Mesa de centro..." 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <hr className="border-gray-100 dark:border-gray-800"/>

            {/* Filtro Categorías */}
            <div className="flex flex-col gap-3">
              <h3 
                className="text-sm font-medium tracking-tight text-gray-900 dark:text-white flex justify-between items-center group cursor-pointer"
                onClick={() => setShowCategoryFilter(!showCategoryFilter)}
              >
                Categorías
                <span className="material-symbols-outlined text-[18px] text-gray-400">
                  {showCategoryFilter ? 'expand_less' : 'expand_more'}
                </span>
              </h3>
              
              {showCategoryFilter && (
                <div className="flex flex-col gap-2.5">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer w-4 h-4"
                      checked={selectedCategory === ''}
                      onChange={() => setSelectedCategory('')}
                    />
                    <span className={`text-sm font-light transition-colors ${selectedCategory === '' ? 'text-primary font-medium' : 'text-gray-600 dark:text-gray-400 group-hover:text-primary'}`}>
                      Todas
                    </span>
                  </label>
                  
                  {categories.map(category => (
                    <label key={category.id} className="flex items-center gap-2.5 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer w-4 h-4"
                        checked={selectedCategory === category.id}
                        onChange={() => setSelectedCategory(category.id)}
                      />
                      <span className={`text-sm font-light transition-colors ${selectedCategory === category.id ? 'text-primary font-medium' : 'text-gray-600 dark:text-gray-400 group-hover:text-primary'}`}>
                        {category.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <hr className="border-gray-100 dark:border-gray-800"/>

            {/* Filtro Precio */}
            <div className="flex flex-col gap-3">
              <h3 
                className="text-sm font-medium tracking-tight text-gray-900 dark:text-white flex justify-between items-center group cursor-pointer"
                onClick={() => setShowPriceFilter(!showPriceFilter)}
              >
                Precio
                <span className="material-symbols-outlined text-[18px] text-gray-400">
                  {showPriceFilter ? 'expand_less' : 'expand_more'}
                </span>
              </h3>
              
              {showPriceFilter && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative w-full">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <input 
                        className="w-full bg-accent-light dark:bg-gray-800 border-none rounded-md text-sm pl-5 py-2 focus:ring-1 focus:ring-primary placeholder:text-gray-400 font-light text-center focus:outline-none" 
                        placeholder="Min" 
                        type="number"
                        min="0"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="relative w-full">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <input 
                        className="w-full bg-accent-light dark:bg-gray-800 border-none rounded-md text-sm pl-5 py-2 focus:ring-1 focus:ring-primary placeholder:text-gray-400 font-light text-center focus:outline-none" 
                        placeholder="Max" 
                        type="number"
                        min="0"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <hr className="border-gray-100 dark:border-gray-800"/>

            {/* Ordenar */}
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-medium tracking-tight text-gray-900 dark:text-white">Ordenar por</h3>
              <div className="relative">
                <select 
                  className="w-full appearance-none bg-accent-light dark:bg-gray-800 border-none rounded-lg text-sm px-4 py-2.5 pr-8 focus:ring-1 focus:ring-primary font-light cursor-pointer focus:outline-none"
                  onChange={handleSortChange}
                  value={
                    sortBy === 'createdAt' ? 'newest' :
                    sortBy === 'price' && sortOrder === 'asc' ? 'price_asc' :
                    sortBy === 'price' && sortOrder === 'desc' ? 'price_desc' :
                    sortBy === 'name' ? 'name_asc' : 'newest'
                  }
                >
                  <option value="relevance">Relevancia</option>
                  <option value="newest">Más Recientes</option>
                  <option value="price_asc">Precio: Menor a Mayor</option>
                  <option value="price_desc">Precio: Mayor a Menor</option>
                  <option value="name_asc">Nombre (A-Z)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <span className="material-symbols-outlined text-[20px]">sort</span>
                </div>
              </div>
              
              <div className="flex gap-2 mt-1">
                <button 
                  onClick={() => setSortOrder('asc')}
                  className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${sortOrder === 'asc' ? 'bg-primary text-white' : 'bg-accent-light dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">arrow_upward</span> Asc
                </button>
                <button 
                  onClick={() => setSortOrder('desc')}
                  className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${sortOrder === 'desc' ? 'bg-primary text-white' : 'bg-accent-light dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">arrow_downward</span> Desc
                </button>
              </div>
            </div>

            <button 
              onClick={clearFilters}
              className="mt-4 w-full py-2.5 bg-gray-900 dark:bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-primary transition-colors tracking-tight"
            >
              Limpiar Filtros
            </button>

          </aside>

          {/* Contenido Principal */}
          <div className="flex-1">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h1 className="text-2xl font-medium tracking-tight text-gray-900 dark:text-white">
                  {currentCategoryName}
                </h1>
                <p className="text-sm font-light text-gray-500 mt-1">
                  Mostrando {filteredProducts.length} resultado(s)
                </p>
              </div>
              <div className="hidden md:flex gap-2">
                <button className="p-1.5 rounded bg-gray-100 dark:bg-gray-800 text-primary transition-colors">
                  <span className="material-symbols-outlined">grid_view</span>
                </button>
                <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-primary transition-colors">
                  <span className="material-symbols-outlined">view_list</span>
                </button>
              </div>
            </div>

            {/* Grid de Productos */}
            {loading ? (
              <div className="w-full py-20 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-r-transparent"></div>
                <p className="mt-4 text-gray-500">Cargando productos...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="w-full py-20 text-center bg-white dark:bg-background-dark rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">search_off</span>
                <p className="text-gray-500">No se encontraron productos con los filtros seleccionados.</p>
                <button onClick={clearFilters} className="mt-4 text-primary hover:underline">
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map(product => {
                   const hasDiscount = product.specialPrice && product.specialPrice < product.price;
                   const discountPercentage = hasDiscount 
                     ? Math.round(((product.price - product.specialPrice) / product.price) * 100)
                     : 0;
                   const isNew = (new Date() - new Date(product.createdAt?.toDate())) / (1000 * 60 * 60 * 24) < 30; // 30 días como "nuevo"

                   return (
                    <Link 
                      key={product.id} 
                      to={`/productos/${product.id}`}
                      className="bg-white dark:bg-background-dark p-3 rounded-xl shadow-sm hover:shadow-md transition-shadow group cursor-pointer border border-gray-50 dark:border-gray-700 flex flex-col h-full"
                    >
                      <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden mb-3 bg-gray-50">
                        {isNew && (
                          <div className="absolute top-2 left-2 bg-primary text-white text-[10px] font-semibold px-2 py-1 rounded tracking-wide uppercase z-20">
                            Nuevo
                          </div>
                        )}
                        {hasDiscount && (
                          <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-semibold px-2 py-1 rounded tracking-wide uppercase z-20">
                            -{discountPercentage}%
                          </div>
                        )}
                        
                        <VariantMediaCarousel
                          media={getVariantMedia(product, { width: 800, height: 1000, crop: 'fill' })}
                          alt={product.name || 'Producto'}
                        />
                        
                        <button 
                          onClick={(e) => handleAddToCart(e, product)}
                          className="absolute bottom-3 right-3 size-9 bg-white dark:bg-gray-800 rounded-full shadow-sm flex items-center justify-center text-text-dark dark:text-text-light opacity-0 group-hover:opacity-100 transition-all hover:bg-primary hover:text-white translate-y-2 group-hover:translate-y-0 z-20"
                        >
                          <span className="material-symbols-outlined text-[18px]">add_shopping_cart</span>
                        </button>
                      </div>
                      
                      <div className="flex flex-col flex-1">
                        <h4 className="font-medium text-text-dark dark:text-text-light text-base mb-1 truncate tracking-tight">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-400 mb-2 font-light">
                          {product.categoryName || categories.find(c => c.id === product.categoryId)?.name || 'Colección'}
                        </p>
                        <div className="mt-auto flex items-center gap-2">
                          {hasDiscount ? (
                            <>
                              <span className="text-red-600 font-semibold tracking-tight text-sm">
                                ${product.specialPrice}
                              </span>
                              <span className="text-gray-400 text-xs line-through tracking-tight font-light">
                                ${product.price}
                              </span>
                            </>
                          ) : (
                            <span className="text-text-dark dark:text-text-light font-semibold tracking-tight text-sm">
                              ${product.price}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default Products
